import { NoteDto } from '@lmt/shared-types';
import { IconButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import NoteEditIcon from '@lmt/unicorn-shared/app/icons/note-edit.svg?react';
import NoteIcon from '@lmt/unicorn-shared/app/icons/note.svg?react';
import NoteUrgentIcon from '@lmt/unicorn-shared/app/icons/note-urgent.svg?react';
import React, { FC, useCallback, useMemo } from 'react';
import styles from './NotePopup.module.scss';

interface Props {
  notes: NoteDto[];
  handleClose?: () => void;
}

export const NotePopup: FC<Props> = (props) => {
  const { notes, handleClose } = props;
  const forms = useGlobalForms();
  const i18next = useScopedTranslation('notes');

  const notesByDate = useMemo(() => {
    const result: Record<string, NoteDto[]> = {};
    notes
      ?.sort((x) => (x.urgent ? -1 : 1))
      .forEach((item) => {
        if (!item.startDate) {
          return;
        }
        const key =
          localFormat(item.startDate, 'LLLL dd') +
          (item.endDate ? ` — ${localFormat(item.endDate, 'LLLL dd')}` : '');
        if (!result[key]) {
          result[key] = [];
        }
        result[key].push(item);
      });
    return result;
  }, [notes]);

  const renderNotesForDate = useCallback(
    (noteDateAsKey: string) => {
      const notes = notesByDate[noteDateAsKey];
      return (
        <div key={noteDateAsKey} className={styles.itemContainer}>
          <span>{noteDateAsKey}</span>
          {notes.map((note) => (
            <div className={styles.row} key={note.id}>
              {note.urgent ? (
                <NoteUrgentIcon className={styles.urgentNoteIcon} />
              ) : (
                <NoteIcon className={styles.noteIcon} />
              )}
              <div className={styles.column}>
                {note.urgent && (
                  <span className={styles.urgentText}>
                    {i18next.t('urgent')}
                  </span>
                )}
                <div className={styles.noteTextContainer}>
                  <span className={styles.noteText}>{note.text}</span>
                </div>
              </div>
              <IconButton
                iconComponent={<NoteEditIcon className={styles.icon} />}
                onClick={() => {
                  handleClose?.();
                  forms.openNoteEditing(note.id);
                }}
              />
            </div>
          ))}
        </div>
      );
    },
    [notesByDate],
  );

  return (
    <div className={styles.container}>
      {Object.keys(notesByDate).map((date) => {
        return renderNotesForDate(date);
      })}
    </div>
  );
};
